import * as React from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { Button, Tabs, TabsContent, TabsList, TabsTrigger, useToast } from "@racoon/ui";

import { useSetJobConsignmentNotesMutation } from "~/shared/api/consignment-note/consignment-note.grpc";
import {
  JOB_INCIDENT_SAVE,
  useCreateJobIncidentMutation,
} from "~/shared/api/driver-incidents/driver-incidents.grpc";
import { useUpdateJobMutation } from "~/shared/api/jobs/job.grpc";
import { useGetRouteQuery } from "~/shared/api/routes/route.grpc";
import { incidentNoteLabel } from "~/shared/lib/job-incident";
import { jobOrgIds } from "~/shared/lib/job-status";
import { AdditionalPhotosTab } from "./additional-photos.tab";
import { ContainerCoefficientsBar } from "./container-coefficients.bar";
import { GpsTracksTab } from "./gps-tracks.tab";
import { JobIncidentBadge } from "./job-incident.badge";
import { JobIncidentSelect } from "./job-incident.select";
import { JobStatusSelect } from "./job-status.select";
import { MswReportInfo } from "./msw-report.info";
import { PhotoReportTab } from "./photo-report.tab";
import { ShareReportDialog } from "./share-report.dialog";
import { hasContainers } from "../model/container-volume";
import { isMswJob } from "../model/is-msw-job";
import { takesIncident, type StagedIncident } from "../model/incident-correction";
import { buildVerdictUpdate, leavesNotCompleted, type Outcome } from "../model/outcome-correction";
import { canShareReport } from "../model/share-report";
import { useCancelRetryJob, type RetryCancel } from "../model/use-cancel-retry-job";
import { useReportIncident } from "../model/use-report-incident";

type Props = {
  job: ConnectProto.Job;
  ownerOrganizationId: number | null;
  /** Container facts not written yet; they go out with the review status. */
  isDraft: boolean;
  onChange: (job: ConnectProto.Job) => void;
  onDraft: (job: ConnectProto.Job) => void;
  /** Opens the route jobs modal, which needs the router the modal body is outside of. */
  onOpenRoute: (route: ConnectProto.Route) => void;
};

/** The job report body — for every job type, MSW (ТБО) or not. */
export const MswReportBody: React.FC<Props> = ({
  job,
  ownerOrganizationId,
  isDraft,
  onChange,
  onDraft,
  onOpenRoute,
}) => {
  const t = useT();
  const isMsw = isMswJob(job);
  const { toast } = useToast();
  const [updateJob] = useUpdateJobMutation();
  const [setJobNotes] = useSetJobConsignmentNotesMutation();
  const [createJobIncident] = useCreateJobIncidentMutation({ fixedCacheKey: JOB_INCIDENT_SAVE });
  // One save at a time, the retry cancel's calls included; the ref stops a same-tick double click
  const saving = React.useRef(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const cancelRetryJob = useCancelRetryJob();
  // Each job remounts the body, so stepping to another job drops unsaved picks
  const [stagedOutcome, setStagedOutcome] = React.useState<Outcome>();
  const [pickedIncident, setPickedIncident] = React.useState<StagedIncident>();
  const incident = useReportIncident(job, stagedOutcome, pickedIncident);
  const savedLabel = incident.savedNote && incidentNoteLabel(t, incident.savedNote);
  const hasLocationPhotos = (job.fileReports ?? []).some(
    (photo) => photo.type === ConnectProto.FileUploadType.PICKUP_LOCATION_VIEW,
  );
  const { data: route } = useGetRouteQuery(
    job.routeId && ownerOrganizationId
      ? { id: job.routeId, ownerOrganizationId: BigInt(ownerOrganizationId) }
      : skipToken,
  );

  const saveVerdict = async (status: ConnectProto.DispatcherReviewStatus) => {
    const update = buildVerdictUpdate(job, status, stagedOutcome);
    const reason = incident.toSave;
    let reviewed = job;
    let retry: RetryCancel | undefined;
    try {
      // The dispatcher's container facts count only once the report is reviewed.
      // Written first: a failed write leaves the status unchanged, and a saved one
      // is kept even if the status fails.
      if (isDraft) {
        const { jobs } = await setJobNotes({
          serviceProviderId: job.serviceProviderId,
          notes: [{ jobId: job.id, containerCoefficients: job.containerCoefficients }],
        }).unwrap();
        const saved = jobs.find((j) => j.id === job.id);
        if (saved) {
          reviewed = {
            ...job,
            containerCoefficients: saved.containerCoefficients,
            numOfContainer: saved.numOfContainer,
            factTotalVolume: saved.factTotalVolume,
          };
        }
        onChange(reviewed);
      }
      if (leavesNotCompleted(job, update.status)) {
        retry = await cancelRetryJob(job);
        if (!retry.ok) return;
      }
      await updateJob({ ...update, ...jobOrgIds(job) }).unwrap();
    } catch {
      // Already toasted by the global error middleware; a retry cancelled for nothing is told
      if (retry?.ok && retry.cancelled) {
        toast({
          variant: "destructive",
          title: t(Namespace.PhotoReport, "retryCancelledStatusFailed", {
            jobNumber: job.orderNumber,
          }),
        });
      }
      return;
    }
    toast({ title: t(Namespace.PhotoReport, "statusUpdated") });
    // The report stays open; the header's badge picks the new status up from here.
    onChange({ ...reviewed, ...update });
    // After the status, so a job just turned Невывоз takes it; a failure keeps the pick
    if (!reason) return;
    try {
      await createJobIncident({
        jobId: BigInt(job.id),
        ownerOrganizationId: BigInt(job.serviceProviderId),
        ...reason,
      }).unwrap();
    } catch {
      toast({ variant: "destructive", title: t(Namespace.PhotoReport, "incidentSaveFailed") });
    }
  };

  const handleUpdateStatus = async (status: ConnectProto.DispatcherReviewStatus) => {
    if (saving.current) return;
    saving.current = true;
    setIsUpdating(true);
    try {
      await saveVerdict(status);
    } finally {
      saving.current = false;
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-[40rem] min-h-0 border-t border-border-primary">
        <MswReportInfo
          job={job}
          route={route}
          onOpenRoute={onOpenRoute}
          statusSelect={
            <JobStatusSelect
              job={job}
              staged={stagedOutcome}
              onStage={setStagedOutcome}
              disabled={isUpdating}
            />
          }
          incidentSelect={
            takesIncident(incident.outcome) && (
              <JobIncidentSelect
                current={incident.note}
                staged={incident.staged}
                onStage={setPickedIncident}
                notCompleted={incident.outcome === ConnectProto.JobStatus.NOT_COMPLETED}
                disabled={isUpdating}
              />
            )
          }
        />
        <Tabs defaultValue="photo" className="flex min-w-0 flex-1 flex-col gap-6 p-6">
          <div className="flex items-center justify-between gap-4">
            <TabsList>
              <TabsTrigger value="photo">{t(Namespace.PhotoReport, "photoReport")}</TabsTrigger>
              <TabsTrigger value="gps">{t(Namespace.PhotoReport, "gps")}</TabsTrigger>
              {/* The pickup location's own photos: kept for container jobs, not MSW. */}
              {!isMsw && hasLocationPhotos && (
                <TabsTrigger value="additionalPhotos">
                  {t(Namespace.PhotoReport, "additionalPhotosShort")}
                </TabsTrigger>
              )}
            </TabsList>
            {incident.savedNote && savedLabel && (
              <JobIncidentBadge
                note={incident.savedNote}
                label={savedLabel}
                recordedAt={incident.savedAt}
              />
            )}
          </div>
          <TabsContent
            value="photo"
            className="!mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto data-[state=inactive]:hidden"
          >
            {/* A non-MSW job without containers has no fills to report. */}
            {(isMsw || hasContainers(job)) && (
              <ContainerCoefficientsBar job={job} onChange={onDraft} disabled={isUpdating} />
            )}
            <PhotoReportTab job={job} failureLabel={savedLabel} />
          </TabsContent>
          <TabsContent value="gps" className="!mt-0 min-h-0 flex-1">
            <GpsTracksTab route={route} job={job} className="!mt-0 !h-full !max-h-none" />
          </TabsContent>
          {!isMsw && hasLocationPhotos && (
            <TabsContent
              value="additionalPhotos"
              className="!mt-0 min-h-0 flex-1 overflow-y-auto data-[state=inactive]:hidden"
            >
              <AdditionalPhotosTab job={job} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <div className="flex items-center justify-end gap-2.5 border-t border-border-primary p-4">
        {/* Figma keeps «Поделиться» in place, disabled with nothing to share */}
        <div className="mr-auto">
          <ShareReportDialog job={job} triggerClassName="h-8" disabled={!canShareReport(job)} />
        </div>
        <Button
          id="photo-report-approve-button"
          variant="solidGreen"
          className="h-8"
          disabled={isUpdating || incident.blocksSave}
          onClick={() => handleUpdateStatus(ConnectProto.DispatcherReviewStatus.APPROVED)}
        >
          {t(Namespace.PhotoReport, "approve")}
        </Button>
        <Button
          id="photo-report-suspect-button"
          variant="solidRed"
          className="h-8 !bg-orange-500 hover:!bg-orange-600"
          disabled={isUpdating || incident.blocksSave}
          onClick={() => handleUpdateStatus(ConnectProto.DispatcherReviewStatus.SUSPICIOUS)}
        >
          {t(Namespace.PhotoReport, "suspicious")}
        </Button>
        <Button
          id="photo-report-reject-button"
          variant="solidRed"
          className="h-8"
          disabled={isUpdating || incident.blocksSave}
          onClick={() => handleUpdateStatus(ConnectProto.DispatcherReviewStatus.REJECTED)}
        >
          {t(Namespace.PhotoReport, "incorrect")}
        </Button>
      </div>
    </div>
  );
};
